/**
 * ScoreSection — оценка кабинета.
 *
 * Первое, что читается, — вердикт словами. Число само по себе ничего не
 * говорит человеку, который видит шкалу впервые: «требует срочного
 * вмешательства» он поймёт мгновенно, «26» — нет. Балл идёт следом как
 * подтверждение, а линейка рядом показывает, где проходит норма.
 *
 * Вердикт и подписи полос выводятся из кода зоны (`content.ts`): API
 * присылает границы и попадание, слова — наши.
 *
 * Разбор балла спрятан под раскрытие: отчёт ежемесячный, и к третьему разу
 * вывод формулы уже никому не нужен.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {
  SCORE_ZONE_TONE,
  SCORE_ZONE_VERDICT,
  paletteTone,
  scorePartLabel,
  scorePartTone,
  type PaletteTone,
} from '../content';
import {TABULAR} from '../helpers';
import type {ScorePart, ScoreZone, ScoreZoneKey} from '../types';
import {Disclosure} from './Disclosure';
import {MeterBar} from './MeterBar';
import {ReportCard} from './ReportCard';
import {ScoreGauge} from './ScoreGauge';
import {ScoreScale} from './ScoreScale';

export interface ScoreSectionProps {
  value: number;
  zones: ScoreZone[];
  /** Полоса, в которую попал балл. Без неё вердикт выводим по границам. */
  zone?: ScoreZoneKey | null;
  parts?: ScorePart[] | null;
  /**
   * Запасная заметка слагаемого `ACTIVITY`. Нужна там, где балл полон не
   * по заслуге, а из-за отсутствия данных: заметку обычно пишет
   * генератор, но при `narrativeFailed` её нет. Заметка генератора всегда
   * сильнее запасной.
   */
  activityNote?: string | null;
}

const Verdict = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone}>(({theme, $tone}) => ({
  ...text('heading.xl'),
  color: theme.palette[$tone].main,
}));

const GaugeRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

const Cell = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurface'),
});

const CellMuted = styled(Box)({
  ...text('body.m'),
  ...TABULAR,
  color: surfaceVar('onSurfaceLight'),
});

const BreakdownNote = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

const Divider = styled(Box)({
  height: 1,
  backgroundColor: surfaceVar('surface2'),
  marginTop: spacingPx('xs'),
  marginBottom: spacingPx('xs'),
});

/** Зона по границам — на случай, если `scoreZone` не пришёл. */
function zoneFor(value: number, zones: ScoreZone[]): ScoreZone | undefined {
  return zones.find(z => value >= z.from && value <= z.to);
}

/**
 * Заметка слагаемого: своя, а для `ACTIVITY` — запасная, если своей нет.
 * Ровно одно слагаемое умеет быть полным не по заслуге, поэтому обобщать
 * запасные заметки на весь разбор незачем.
 */
function partNote(part: ScorePart, activityNote?: string | null): string | null {
  return part.note ?? (part.key === 'ACTIVITY' ? (activityNote ?? null) : null);
}

export function ScoreSection({value, zones, zone, parts, activityNote}: ScoreSectionProps) {
  const currentZone = zone ?? zoneFor(value, zones)?.key;
  // Верх шкалы — верхняя граница последней полосы. Сумма потолков
  // слагаемых должна ей равняться, но кольцо на это не полагается:
  // разъехавшийся разбор не должен ломать шкалу.
  const max = zones.reduce((top, z) => Math.max(top, z.to), 0);
  const totalGot = parts?.reduce((sum, part) => sum + part.got, 0) ?? value;
  const totalMax = parts?.reduce((sum, part) => sum + part.max, 0) ?? max;

  return (
    <ReportCard padding='l'>
      {/* Вердикт, измерение и его состав — три отдельные мысли, поэтому
          разведены сильнее, чем строки внутри каждой из них. */}
      <Stack gap='xl'>
        {currentZone && (
          <Verdict $tone={paletteTone(SCORE_ZONE_TONE[currentZone])}>
            {SCORE_ZONE_VERDICT[currentZone]}
          </Verdict>
        )}

        {/* Кольцо по центру, линейка во всю ширину под ним: так балл
            читается как итог, а шкала — как основание под ним. */}
        <Stack gap='xl'>
          <GaugeRow>
            <ScoreGauge value={value} max={max} zones={zones} size={260} />
          </GaugeRow>
          <ScoreScale value={value} max={max} zones={zones} />
        </Stack>

        {parts && parts.length > 0 && (
          <Disclosure label='Из чего сложился балл' labelOpen='Свернуть'>
            {/* Слагаемые — самостоятельные пункты, и разрыв между ними
                должен читаться крупнее, чем строки внутри одного пункта. */}
            <Stack gap='l'>
              {parts.map(part => {
                const note = partNote(part, activityNote);
                return (
                  <Stack key={part.key} gap='xs'>
                    <Row gap='s' alignY='center' justify='between'>
                      <Cell>{scorePartLabel(part.key)}</Cell>
                      <CellMuted>
                        {part.got} / {part.max}
                      </CellMuted>
                    </Row>
                    <MeterBar
                      value={part.max > 0 ? part.got / part.max : 0}
                      tone={scorePartTone(part.got, part.max)}
                      height={6}
                    />
                    {note && <BreakdownNote>{note}</BreakdownNote>}
                  </Stack>
                );
              })}
              <Divider />
              <Row gap='s' justify='between'>
                <Cell style={{...text('label.l')}}>Итого</Cell>
                <Cell style={{...text('label.l'), ...TABULAR}}>
                  {totalGot} / {totalMax}
                </Cell>
              </Row>
            </Stack>
          </Disclosure>
        )}
      </Stack>
    </ReportCard>
  );
}
